import { useEffect, useMemo, useState } from "react";
import { useApp } from "../store";
import { derive } from "../lib/derive";
import { TIER_LABELS } from "../lib/fitTier";
import { orDash, pct } from "../lib/format";
import { type School } from "../lib/types";
import { Flags, ScholarshipDollars, StudentLoveHearts, TierBadge } from "../components/badges";
import { DOLLAR_LABEL } from "../lib/scholarship";
import { StatusControl } from "../components/StatusControl";
import { InteractionLog } from "../components/InteractionLog";
import { ScorecardPanel } from "../components/ScorecardPanel";
import { BrandBar, SchoolLogo } from "../components/SchoolBrand";

export default function SchoolModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { byId, settings, putEvaluation } = useApp();
  const school = byId(id);
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);

  useEffect(() => {
    if (school) setNotes(school.evaluation.notes);
    setNotesDirty(false);
  }, [school?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const d = useMemo(() => (school && settings ? derive(school, settings) : null), [school, settings]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-2 sm:p-6"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl rounded-xl bg-white p-4 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-white/80 px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          ✕
        </button>

        {!school || !settings || !d ? (
          <p className="p-4 text-slate-500">School not found.</p>
        ) : (
          <SchoolBody
            school={school}
            d={d}
            settings={settings}
            notes={notes}
            setNotes={setNotes}
            notesDirty={notesDirty}
            setNotesDirty={setNotesDirty}
            putEvaluation={putEvaluation}
          />
        )}
      </div>
    </div>
  );
}

type BodyProps = {
  school: School;
  d: NonNullable<ReturnType<typeof derive>>;
  settings: NonNullable<ReturnType<typeof useApp>["settings"]>;
  notes: string;
  setNotes: (s: string) => void;
  notesDirty: boolean;
  setNotesDirty: (b: boolean) => void;
  putEvaluation: ReturnType<typeof useApp>["putEvaluation"];
};

function SchoolBody({
  school,
  d,
  settings,
  notes,
  setNotes,
  notesDirty,
  setNotesDirty,
  putEvaluation,
}: BodyProps) {
  const ev = school.evaluation;

  return (
    <div className="pb-2">
      <div className="flex flex-wrap items-start gap-3 pr-8">
        <SchoolLogo school={school} size={48} />
        <div>
          <h2 className="text-xl font-bold sm:text-2xl">{school.name}</h2>
          <div className="text-sm text-slate-600">
            {school.division} · {orDash(school.conference)}
            {school.conference_note && (
              <span className="ml-1 text-xs text-amber-600" title={school.conference_note}>
                (affiliate)
              </span>
            )}{" "}
            · {orDash(school.city)} {orDash(school.state)}
          </div>
        </div>
        <div className="ml-auto text-right">
          <StatusControl
            value={ev.status}
            size="md"
            onChange={(s) => void putEvaluation(school.id, { status: s })}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <LinkButton href={school.rowing_website} label="Rowing program" primary />
        <LinkButton href={school.recruiting_form_url} label="Recruiting form" primary />
        <LinkButton href={school.school_website} label="School site" />
        <LinkButton href={school.scorecard_url} label="College Scorecard" />
      </div>

      <div className="mt-3">
        <BrandBar school={school} />
      </div>
      <Flags flags={school.data_flags} />

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <AiSummaryCard summary={school.ai_summary} />

        <ClaudePickCard school={school} />

        {/* Katie's notes & rank */}
        <section className="card">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Katie's notes</h3>
            <label className="flex items-center gap-2 text-sm">
              <span className="label">Katie rank</span>
              <RankInput
                value={ev.katieRank}
                onChange={(v) => void putEvaluation(school.id, { katieRank: v })}
              />
            </label>
          </div>
          <textarea
            className="input mt-2 h-40 w-full"
            placeholder="Notes on this school…"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setNotesDirty(true);
            }}
            onBlur={() => {
              if (notesDirty) {
                void putEvaluation(school.id, { notes });
                setNotesDirty(false);
              }
            }}
          />
          <label className="mt-3 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={ev.visitPlanned}
              onChange={(e) => void putEvaluation(school.id, { visitPlanned: e.target.checked })}
            />
            Campus visit planned
          </label>
          <label className="mt-2 flex items-center gap-2 text-sm">
            <span className="label">Planned visit date</span>
            <input
              type="date"
              className="input"
              value={ev.visitDate ?? ""}
              onChange={(e) => void putEvaluation(school.id, { visitDate: e.target.value || null })}
            />
          </label>
        </section>

        <DadPickCard school={school} />

        {/* Rowing */}
        <section className="card">
          <h3 className="font-semibold">Rowing program</h3>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <Row label="Fit tier">
              <TierBadge tier={d.tier} />
            </Row>
            <Row label="cMax (all divisions)">
              {school.cmax_secs_back_combined === null
                ? "no data"
                : `+${school.cmax_secs_back_combined.toFixed(1)}s (${orDash(school.cmax_rank_combined_raw)})`}
              {school.cmax_estimated && (
                <span className="ml-1 text-[10px] font-medium text-amber-600">estimate</span>
              )}
            </Row>
            <Row label="cMax (within division)">{orDash(school.cmax_rank_div_raw)}</Row>
            <Row label="Est. 1V 8+ 2k">{orDash(school.est_8_2k_time)}</Row>
            <Row label="1V spring race time">{orDash(school.v8_race_time)}</Row>
            <Row label="Race source">{orDash(school.v8_race_time_source)}</Row>
          </dl>
          <p className="mt-2 text-xs text-slate-500">
            {TIER_LABELS[d.tier]} — thresholds: Dream &lt; {settings.assumptions.tierThresholds.dream}s,
            Stretch &lt; {settings.assumptions.tierThresholds.stretch}s, Target ≤{" "}
            {settings.assumptions.tierThresholds.target}s back (vs Katie's projected pace).
          </p>
          {school.cmax_note && (
            <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-800">
              <span className="font-semibold">cMax estimate — </span>
              {school.cmax_note}
            </p>
          )}
          {school.program_notes && (
            <p className="mt-2 border-t border-slate-100 pt-2 text-sm text-slate-600">
              {school.program_notes}
            </p>
          )}
        </section>

        <PtCard school={school} />

        <CoachCard school={school} />

        <ScholarshipCard school={school} />

        <StudentLoveCard school={school} />

        <ScorecardPanel school={school} />
      </div>

      <div className="mt-4">
        <InteractionLog school={school} />
      </div>
    </div>
  );
}

function RankInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [text, setText] = useState(value == null ? "" : String(value));
  useEffect(() => {
    setText(value == null ? "" : String(value));
  }, [value]);
  return (
    <input
      type="number"
      step="0.1"
      min="0"
      inputMode="decimal"
      className="input w-20 text-right"
      placeholder="—"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const t = text.trim();
        const n = t === "" ? null : Number(t);
        if (n === null || (Number.isFinite(n) && n >= 0)) onChange(n);
        else setText(value == null ? "" : String(value));
      }}
    />
  );
}

function LinkButton({
  href,
  label,
  primary,
}: {
  href: string | null;
  label: string;
  primary?: boolean;
}) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm transition ${
        primary
          ? "bg-blue-600 text-white hover:bg-blue-700"
          : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
      <span aria-hidden className="text-base leading-none">
        ↗
      </span>
    </a>
  );
}

function AiSummaryCard({ summary }: { summary: School["ai_summary"] }) {
  if (!summary) return null;
  return (
    <details className="card group lg:col-span-2">
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
        <span className="flex items-center gap-2">
          <svg
            className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M6 8l4 4 4-4" />
          </svg>
          <h3 className="font-semibold">Summary</h3>
        </span>
        <span className="text-xs text-slate-400">researched {summary.updated}</span>
      </summary>

      <p className="mt-3 border-l-2 border-blue-500 pl-3 text-sm font-medium text-slate-800">
        {summary.headline}
      </p>

      <div className="mt-3 space-y-3">
        {summary.sections.map((s) => (
          <div key={s.heading}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {s.heading}
            </h4>
            {s.body.split("\n\n").map((p, i) => (
              <p key={i} className="mt-1 text-sm text-slate-700">
                {p}
              </p>
            ))}
          </div>
        ))}
      </div>

      {summary.sources && summary.sources.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Sources ({summary.sources.length})
          </div>
          <ul className="mt-2 space-y-1 text-xs">
            {summary.sources.map((u) => (
              <li key={u}>
                <a href={u} target="_blank" rel="noreferrer" className="break-all">
                  {u}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-2 text-xs text-slate-400">
        Compiled from public web sources — a starting point, not a substitute for talking to the
        coach and visiting.
      </p>
    </details>
  );
}

function ClaudePickCard({ school }: { school: School }) {
  const p = school.claude_pick;
  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Claude's top 25</h3>
        {p ? (
          <span className="rounded bg-violet-100 px-2 py-0.5 text-sm font-semibold text-violet-700">
            #{p.rank} of 25
          </span>
        ) : (
          <span className="text-xs text-slate-400">not in the top 25</span>
        )}
      </div>
      {p ? (
        <p className="mt-2 text-sm text-slate-700">{p.note}</p>
      ) : (
        <p className="mt-2 text-sm text-slate-500">
          Not among Claude's 25 best-fit picks for Katie. The Summary above has the full assessment.
        </p>
      )}
      <p className="mt-2 text-xs text-slate-400">
        Claude's ranking, weighing the PT pathway, how realistically Katie could make a boat and race
        at 5'4&quot;, and school quality and cost — a starting point, not a verdict.
      </p>
    </section>
  );
}

function DadPickCard({ school }: { school: School }) {
  const { putEvaluation } = useApp();
  const ev = school.evaluation;
  const [why, setWhy] = useState(ev.dadPickWhy);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setWhy(school.evaluation.dadPickWhy);
    setDirty(false);
  }, [school.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">
          Dad's pick {ev.dadPick && <span className="text-emerald-600">✓</span>}
        </h3>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={ev.dadPick}
            onChange={(e) => void putEvaluation(school.id, { dadPick: e.target.checked })}
          />
          Dad likes this one
        </label>
      </div>
      <textarea
        className="input mt-2 h-28 w-full"
        placeholder="Why this one…"
        value={why}
        onChange={(e) => {
          setWhy(e.target.value);
          setDirty(true);
        }}
        onBlur={() => {
          if (dirty) {
            void putEvaluation(school.id, { dadPickWhy: why });
            setDirty(false);
          }
        }}
      />
    </section>
  );
}

function PtCard({ school }: { school: School }) {
  const status = school.pt_program;
  const majors = school.pt_majors ?? [];
  return (
    <section className="card">
      <h3 className="font-semibold">Physical therapy path</h3>
      <div className="mt-2 flex items-start gap-2 text-sm">
        {status === "has_pt" ? (
          <>
            <span className="text-emerald-600">✓</span>
            <span className="font-medium">
              Clinical rehab / therapy doctorate on campus — likely a DPT
            </span>
          </>
        ) : status === "related" ? (
          <>
            <span className="text-amber-600">✓</span>
            <span className="font-medium">Pre-PT majors only — no doctorate program</span>
          </>
        ) : (
          <span className="text-slate-500">No PT or pre-PT programs in the federal data</span>
        )}
      </div>
      {majors.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
          {majors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      <p className="mt-2 text-xs text-slate-400">
        From College Scorecard program data (CIP "Rehabilitation &amp; Therapeutic Professions" /
        "Kinesiology &amp; Exercise Science"). The doctorate flag is a proxy — confirm the specific
        DPT on the school's site.
      </p>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="label">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ScholarshipCard({ school }: { school: School }) {
  const { patchSchool } = useApp();
  const [edit, setEdit] = useState(false);
  const n = school.scholarship_dollars ?? 0;
  const overridden = school._edited?.includes("scholarship_dollars");
  const canAdjust = n > 0 || overridden; // DIII / Ivy / academies have no athletic aid to rate

  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Scholarship outlook</h3>
        {canAdjust && (
          <button className="btn" onClick={() => setEdit((v) => !v)}>
            {edit ? "Done" : "Adjust"}
          </button>
        )}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <span className="text-lg">
          <ScholarshipDollars school={school} />
        </span>
        <span className="text-sm font-medium text-slate-700">
          {school.scholarship_label || DOLLAR_LABEL[n] || "—"}
        </span>
        {overridden && <span className="text-[10px] text-amber-600">(set by hand)</span>}
      </div>

      {edit && canAdjust && (
        <div className="mt-2 flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded border border-slate-300">
            {[1, 2, 3, 4, 5].map((v) => (
              <button
                key={v}
                onClick={() => void patchSchool(school.id, { scholarship_dollars: v })}
                className={`w-8 py-0.5 text-xs ${v > 1 ? "border-l border-slate-300" : ""} ${
                  v === n ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                {"$".repeat(v)}
              </button>
            ))}
          </div>
          {overridden && (
            <button
              className="btn"
              onClick={() => void patchSchool(school.id, { scholarship_dollars: null })}
            >
              Reset to auto
            </button>
          )}
        </div>
      )}

      <dl className="mt-3 space-y-1 text-sm">
        <Row label="What this program can offer">{orDash(school.scholarship_cap_label)}</Row>
        {!school.scholarship_label && (
          <Row label="Athletic $ potential (early estimate)">
            {orDash(school.athletic_dollar_potential)}
          </Row>
        )}
      </dl>
      {school.scholarship_basis && (
        <p className="mt-2 text-xs text-slate-500">Why: {school.scholarship_basis}</p>
      )}
      {!school.scholarship_label && (
        <p className="mt-1 text-xs text-slate-400">
          Rough guide only — a coach's real offer depends on Katie's times and the class they're
          recruiting.
        </p>
      )}
    </section>
  );
}

function StudentLoveCard({ school }: { school: School }) {
  const love = school.student_love;
  const pp =
    school.retention_vs_expected == null ? null : Math.round(school.retention_vs_expected * 100);

  return (
    <section className="card">
      <h3 className="font-semibold">
        Student Love <span className="align-middle text-rose-500">♥</span>
      </h3>

      {love == null ? (
        <p className="mt-2 text-sm text-slate-400">
          Needs both an acceptance rate and a first-year retention rate — not available for this
          school.
        </p>
      ) : (
        <>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-lg">
              <StudentLoveHearts school={school} />
            </span>
            {pp != null && (
              <span className="text-sm font-medium text-slate-700">
                retention {pp >= 0 ? "+" : ""}
                {pp}pp vs. expected
              </span>
            )}
          </div>
          {school.student_love_basis && (
            <p className="mt-2 text-sm text-slate-600">{school.student_love_basis}</p>
          )}
          <dl className="mt-3 grid grid-cols-3 gap-x-3 text-sm">
            <Row label="Return after year 1">{pct(school.retention_rate)}</Row>
            <Row label="Expected for its selectivity">{pct(school.retention_expected)}</Row>
            <Row label="Acceptance rate">{pct(school.admission_rate)}</Row>
          </dl>
        </>
      )}

      <details className="mt-3 border-t border-slate-100 pt-2">
        <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-slate-500">
          How this score works
        </summary>
        <div className="mt-2 space-y-2 text-xs text-slate-500">
          <p>
            The share of first-year students who come back for sophomore year is the best single
            signal that students are happy where they landed — leaving after one year is the
            clearest "this isn't for me" vote.
          </p>
          <p>
            But raw retention mostly reflects <em>who a school admits</em>: selective, wealthy
            schools retain ~97% almost automatically. So we fit a curve to all {"~150"} rowing
            schools — retention as a function of acceptance rate — and score each school on how far
            it beats (or trails) that expectation, in percentage points.
          </p>
          <p>
            Hearts: <strong>5</strong> = +8pp or better, <strong>4</strong> = +3 to +8,{" "}
            <strong>3</strong> = within ±3 (about as expected), <strong>2</strong> = −3 to −8,{" "}
            <strong>1</strong> = −8 to −15, <strong>0</strong> = worse than −15pp.
          </p>
          <p>
            Read it <em>within</em> a selectivity tier. A school retaining 93% while admitting 80%
            (Gonzaga, Creighton) is telling you something real; a hyper-selective school at 97% is
            mostly showing you its admissions funnel.
          </p>
        </div>
      </details>
    </section>
  );
}

function CoachCard({ school }: { school: School }) {
  const { patchSchool } = useApp();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    head_coach_name: school.head_coach_name ?? "",
    head_coach_email: school.head_coach_email ?? "",
    head_coach_phone: school.head_coach_phone ?? "",
    recruiting_form_url: school.recruiting_form_url ?? "",
    school_website: school.school_website ?? "",
    rowing_website: school.rowing_website ?? "",
  });

  async function save() {
    await patchSchool(school.id, form);
    setEdit(false);
  }

  return (
    <section className="card">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Coach &amp; links</h3>
        <button className="btn" onClick={() => setEdit((v) => !v)}>
          {edit ? "Cancel" : "Edit"}
        </button>
      </div>
      {edit ? (
        <div className="mt-2 space-y-2">
          {(
            [
              ["head_coach_name", "Head coach name"],
              ["head_coach_email", "Head coach email"],
              ["head_coach_phone", "Phone"],
              ["recruiting_form_url", "Recruiting questionnaire URL"],
              ["school_website", "School website"],
              ["rowing_website", "Rowing program website"],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="block text-sm">
              <span className="label">{label}</span>
              <input
                className="input w-full"
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              />
            </label>
          ))}
          <button className="btn btn-primary" onClick={() => void save()}>
            Save
          </button>
        </div>
      ) : (
        <dl className="mt-2 space-y-1 text-sm">
          <Row label="Head coach">{orDash(school.head_coach_name)}</Row>
          <Row label="Email">
            {school.head_coach_email ? (
              <a href={`mailto:${school.head_coach_email}`}>{school.head_coach_email}</a>
            ) : (
              "—"
            )}
          </Row>
          <Row label="Phone">{orDash(school.head_coach_phone)}</Row>
          <Row label="Recruiting form">
            {school.recruiting_form_url ? (
              <a href={school.recruiting_form_url} target="_blank" rel="noreferrer">
                open ↗
              </a>
            ) : (
              "—"
            )}
          </Row>
        </dl>
      )}
    </section>
  );
}
